import { Header } from "../../components/Header"
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Container, Link, List, ListItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { AIReportData, getAIReport, getLatestAIReportForSupplier, ReportData, requestAIReportForSupplier } from "../../remote/reports";
import Markdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from 'remark-gfm';
import { FQDN } from "../../components/FQDN";
import { Check } from "@mui/icons-material";

export interface MarkdownReportProps {
	markdown: string
}

export const MarkdownReport = ({markdown}: MarkdownReportProps) => {
	return <Markdown remarkPlugins={[remarkGfm]} components={{
		h1(props) {
			const {node, ...rest} = props
			return <Typography variant="h3" {...rest}/>
		},
		h2(props) {
			const {node, ...rest} = props
			return <Typography variant="h4" {...rest}/>
		},
		h3(props) {
			const {node, ...rest} = props
			return <Typography variant="h5" {...rest}/>
		},
		h4(props) {
			const {node, ...rest} = props
			return <Typography variant="h6" {...rest}/>
		},
		h5(props) {
			const {node, ...rest} = props
			return <Typography variant="h6" {...rest}/>
		},
		h6(props) {
			const {node, ...rest} = props
			return <Typography variant="body1" {...rest}/>
		},
		p(props) {
			const {node, ...rest} = props
			return <Typography variant="body1" {...rest}/>
		},
		text(props) {
			const {node, ...rest} = props
			return <Typography variant="body1" {...rest}/>
		},
		table(props) {
			const {node, ...rest} = props
			return <Table container={Paper} {...rest}/>
		},
		thead(props) {
			const {node, ...rest} = props
			return <TableHead {...rest}/>
		},
		tbody(props) {
			const {node, ...rest} = props
			return <TableBody {...rest}/>
		},
		tr(props) {
			const {node, ...rest} = props
			return <TableRow {...rest}/>
		},
		td(props) {
			const {node, ...rest} = props
			return <TableCell {...rest}/>
		},
		th(props) {
			const {node, ...rest} = props
			return <TableCell {...rest}/>
		},
		a(props) {
			const {node, ...rest} = props
			return <Link {...rest}/>
		},
		ul(props) {
			const {node, ...rest} = props
			return <List sx={{ listStyleType: 'disc' }} {...rest}/>
		},
		li(props) {
			const {node, ...rest} = props
			return <Typography component={ListItem} sx={{display: "list-item"}} variant="body2" {...rest} />
		}
	}} urlTransform={(uri) => {
		if (uri.startsWith("cytrusted://")) {
			const parts = uri.split('/')
			const type = parts[2]
			const id = parts[3]
			const typeCases = new Map([
				["FQDN", (id) => `/tree/fqdn/${id}`],
				["IP", (id) => `/tree/ip/${id}`],
				["PORT", (id) => `/tree/port/${id}`],
				["TECHNOLOGY", (id) => `/tree/technology/${id}`],
				["CERTIFICATE", (id) => `/tree/certificate/${id}`],
				["VULNERABILITY", (id) => `/tree/vulnerability/${id}`],
				["WEAKNESS", (id) => `/tree/weakness/${id}`],
			])
			let func = typeCases.get(type.toUpperCase())
			if (func) {
				return func(id)
			}
		} else {
			return defaultUrlTransform(uri)
		}
	}}>{markdown}</Markdown>
}

export class ReportRecencyButtonProps {
	reportData: AIReportData

	constructor(reportData: AIReportData) {
		this.reportData = reportData;
	}
}

export const ReportRecencyButton = (props: ReportRecencyButtonProps) => {
	const navigate = useNavigate()

	const [reportState, setReportState] = useState<'GENERATING' | 'READY'>('READY')

	const [getReportFunc, requestReportFunc] = (
		props.reportData.report_type == "HOST" ? [getLatestAIReportForSupplier, requestAIReportForSupplier]
		: (props.reportData.report_type == "SUPPLIER" ? [getLatestAIReportForSupplier, requestAIReportForSupplier] : [undefined, undefined])
	)

	const [latestReportData, setLatestReportData] = useState<AIReportData | null>(null);
	useEffect(() => {
		if (getReportFunc) {
			getReportFunc(props.reportData.source, (data, resp) => {
				if (data) {
					setLatestReportData(data);
				} else {
					setLatestReportData(null);
				}
			});
		}
	}, [setLatestReportData]);

	const checkReportStatusPing = useCallback(() => {
		if (getReportFunc) {
			getReportFunc(props.reportData.source, (data, resp) => {
				if (data) {
					if (data.finished_at) {
						setReportState('READY');
					} else {
						setTimeout(checkReportStatusPing, 2000 + 1000 * Math.random())
					}

					if (data.id != latestReportData?.id && data.finished_at != latestReportData?.finished_at) {
						setLatestReportData(data);
					}
				} else {
					setLatestReportData(null);
				}
			})
		}
	}, [latestReportData, setLatestReportData, setReportState])

	useEffect(() => {
		if (latestReportData) {
			if (latestReportData.finished_at == null && reportState != 'GENERATING') {
				setReportState('GENERATING')
			}
		}
	}, [latestReportData, setReportState])

	if (latestReportData) {
		const currentReportOutOfDate = (
		   latestReportData.new_report_possible
		|| latestReportData.finished_at == null
		|| latestReportData.id != props.reportData.id);

		return currentReportOutOfDate
			? ((latestReportData.new_report_possible && reportState != 'GENERATING')
				? <Button onClick={() => {
					setReportState('GENERATING');
					if (requestReportFunc) {
						requestReportFunc(props.reportData.source, (data, resp) => {
							if (data) {
								if (data.finished_at) {
									setReportState('READY');
								} else {
									setTimeout(checkReportStatusPing, 2000 + 1000 * Math.random())
								}
								setLatestReportData(data);
							} else {
								setLatestReportData(null);
							}
						})
					}
				}} variant="contained">
					<Typography variant="body1">Re-generate summary from new data</Typography>
				</Button>
				: ((reportState == 'GENERATING')
					? <Button disabled endIcon={<CircularProgress size='24px' />}>Generating newer report..</Button>
					: (reportState == 'READY') ?(<Button onClick={() => navigate(`/reports/ai/supplier/${latestReportData.id}`, {flushSync: true})}>
						<Typography variant="body1">Go to newer summary</Typography>
					</Button>) : <Typography>Error</Typography>))
			: <Button endIcon={<Check color="action"/>} disabled>Summary generated from up-to-date data</Button>
	} else {
		return <Button endIcon={<CircularProgress />}>Checking for a more recent report</Button>
	}
}

export const BrowseFQDNReport = () => {
	const params = useParams()

	if ( ! params.reportId) {
		return <CircularProgress />
	}

	const [reportData, setReportData] = useState<AIReportData | null>(null);
	useEffect(() => {
		if (params.reportId) {
			getAIReport(params.reportId, (data, resp) => {
				if (data) {
					setReportData(data);
				} else {
					setReportData(null);
				}
			});
		}
	}, [setReportData, params]);

	let reportType: 'HOST' | 'SUPPLIER' = 'HOST';
	if (reportData?.report_type == 'SUPPLIER') {
		reportType = 'SUPPLIER';
	}

	return (reportData ? <>
		<Header />
		<Container>
			<Stack direction='row'>
				<Typography variant="h3">FQDN Report</Typography>
				<Box sx={{ml: 'auto'}} />
				<ReportRecencyButton reportData={reportData}/>
			</Stack>
			<Typography variant="h3">FQDN Report</Typography>
			<br />
			<FQDN id={reportData.source} />
			<br />
			<MarkdownReport markdown={reportData.report_markdown} />
		</Container>
	</> : <CircularProgress />)
}


export const BrowseSupplierReport = () => {
	const params = useParams()

	if ( ! params.reportId) {
		return <CircularProgress />
	}

	const [reportData, setReportData] = useState<AIReportData | null>(null);
	useEffect(() => {
		if (params.reportId) {
			getAIReport(params.reportId, (data, resp) => {
				if (data) {
					setReportData(data);
				} else {
					setReportData(null);
				}
			});
		}
	}, [setReportData, params]);

	return (reportData ? <>
		<Header />
		<Container>
			<Stack direction='row'>
				<Typography variant="h3">Supplier Report</Typography>
				<Box sx={{ml: 'auto'}} />
				<ReportRecencyButton reportData={reportData}/>
			</Stack>
			<br />
			<MarkdownReport markdown={reportData.report_markdown} />
		</Container>
	</> : <CircularProgress />)
}

